import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { MailX, CheckCircle, Loader2, SlidersHorizontal } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const submit = useCallback(async (category = "all") => {
    if (!token && !email.trim()) {
      setError("Enter your email address.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/subscription/unsubscribe`, { email, token, category, reason: "user_unsubscribe" });
      if (response.data.email) setEmail(response.data.email);
      if (response.data.preferences) setPreferences(response.data.preferences);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to unsubscribe right now.");
    } finally {
      setLoading(false);
    }
  }, [email, token]);

  const savePreferences = useCallback(async () => {
    if (!token || !preferences) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/subscription/email-preferences`, { token, preferences });
      setPreferences(response.data.preferences);
      setEmail(response.data.email);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to save preferences right now.");
    } finally {
      setLoading(false);
    }
  }, [preferences, token]);

  useEffect(() => {
    const loadPreferences = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const response = await axios.get(`${API}/subscription/email-preferences`, { params: { token } });
        setEmail(response.data.email);
        setPreferences(response.data.preferences);
      } catch (err) {
        setError(err.response?.data?.detail || "This preference link is invalid or expired.");
      } finally {
        setLoading(false);
      }
    };
    if (token) loadPreferences();
    else if (searchParams.get("email")) submit();
  }, [searchParams, submit, token]);

  const setPreference = (key, value) => {
    setPreferences((prev) => ({ ...(prev || {}), [key]: value }));
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md glass rounded-2xl p-8 border border-white/10 text-center" data-testid="unsubscribe-page">
        {loading && !success && token ? (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-amber-300 animate-spin" />
            <h1 className="text-2xl font-black mb-2">Loading preferences</h1>
          </>
        ) : success ? (
          <>
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
            <h1 className="text-2xl font-black mb-2">Preferences saved</h1>
            <p className="text-white/60 mb-6">{email} email preferences are updated.</p>
            <Link to="/login" data-testid="unsubscribe-back-login-link"><Button className="bg-white text-black hover:bg-white/90">Back to login</Button></Link>
          </>
        ) : token && preferences ? (
          <>
            <SlidersHorizontal className="w-12 h-12 mx-auto mb-4 text-cyan-300" />
            <h1 className="text-2xl font-black mb-2">Email preferences</h1>
            <p className="text-white/60 mb-6">Choose which emails {email} should receive.</p>
            <div className="space-y-3 text-left mb-5" data-testid="email-preferences-form">
              {[
                ["winback", "Win-back offers"],
                ["product", "Product updates"],
                ["weather_alerts", "Weather alert emails"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3" data-testid={`email-preference-row-${key}`}>
                  <span>{label}</span>
                  <input type="checkbox" checked={!!preferences[key]} onChange={(e) => setPreference(key, e.target.checked)} data-testid={`email-preference-toggle-${key}`} />
                </label>
              ))}
            </div>
            {error && <p role="alert" className="text-sm text-red-300 mb-3" data-testid="unsubscribe-error">{error}</p>}
            <div className="grid grid-cols-1 gap-3">
              <Button onClick={savePreferences} disabled={loading} className="w-full bg-cyan-300 text-black hover:bg-cyan-200" data-testid="save-email-preferences-button">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save preferences
              </Button>
              <Button onClick={() => submit("all")} disabled={loading} variant="outline" className="w-full border-white/15 text-white hover:bg-white/10" data-testid="unsubscribe-all-button">
                Unsubscribe from all
              </Button>
            </div>
          </>
        ) : (
          <>
            <MailX className="w-12 h-12 mx-auto mb-4 text-amber-300" />
            <h1 className="text-2xl font-black mb-2">Unsubscribe</h1>
            <p className="text-white/60 mb-6">Stop marketing and win-back emails from TheForecastTracker.</p>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="bg-white/5 border-white/10 mb-3" data-testid="unsubscribe-email-input" />
            {error && <p role="alert" className="text-sm text-red-300 mb-3" data-testid="unsubscribe-error">{error}</p>}
            <Button onClick={() => submit("all")} disabled={loading} className="w-full bg-amber-300 text-black hover:bg-amber-200" data-testid="unsubscribe-submit-button">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Unsubscribe
            </Button>
          </>
        )}
      </div>
    </div>
  );
}